import ErrorBoundary from 'components/error-boundary/error-boundary.component';
import { useDefaultOnReset } from 'components/error-boundary/error-boundary.hooks';
import CenteredProgress from 'components/progress/centered-progress/centered-progress.component';
import { ReactElement, Suspense } from 'react';
import { useLocation } from 'react-router-dom';

type RouteWrapperProps = {
  element?: ReactElement;
};

/**
 * Wraps a route element in a Suspense fallback and a route-level error boundary.
 *
 * The boundary is keyed on the pathname so a crash on one route does not latch
 * across navigation. When the matched route swaps, React Router renders another
 * `<RouteWrapper>` in the same slot, so React reconciles it as the *same*
 * boundary instance (same component type, same position) rather than mounting a
 * fresh one — and react-error-boundary keeps showing its fallback until a
 * `resetKeys` entry changes. Without the pathname key, one route's error poisons
 * every sibling route until a full reload (e.g. a Guest Journey tab throwing
 * took down all the other tabs until logout).
 *
 * The reset only fires while the boundary is already in its error state, so
 * normal navigation is untouched. Navigation-driven resets (`reason: 'keys'`)
 * are silent; only the explicit "reset" link (`reason: 'imperative-api'`) runs
 * the heavy recovery (persistor purge + history reset), which we don't want
 * firing every time the user simply navigates away from a broken route.
 */
const RouteWrapper = ({ element }: RouteWrapperProps) => {
  const { pathname } = useLocation();
  const defaultOnReset = useDefaultOnReset();

  return (
    <ErrorBoundary
      name="RouteWrapper"
      resetKeys={[pathname]}
      onReset={details => {
        if (details.reason !== 'keys') defaultOnReset();
      }}
    >
      <Suspense fallback={<CenteredProgress />}>{element}</Suspense>
    </ErrorBoundary>
  );
};

export default RouteWrapper;
